import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { List as ImmutableList } from 'immutable';
import { Box, InputAdornment, makeStyles, MenuItem, Paper, Typography } from '@material-ui/core';

import {
  IPackageInstanceSubscription,
  IPaymentAccount,
  IPaymentAccountImt,
} from 'common/components/PersonProfile/interfaces';
import { getCreditCardIcon } from 'common/components/CreditCardData/constants';
import { getExpiryDate } from 'common/utils/time';
import { getAccessByPropPath } from 'common/utils/errorObject';
import { CustomTheme } from 'common/ui/interfaces';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { FormattedMessage } from 'react-intl';
import commonMessages from '../../../../messages/messages';
import TextField from 'common/components/TextField/TextField';
import Autocomplete from 'common/components/Autocomplete/Autocomplete';

interface IPaymentAccountFieldProps {
  storedCreditCards: ImmutableList<IPaymentAccountImt>;
  isNewCardInputDisabled: boolean;
  index: number;
  subscription: IPackageInstanceSubscription;
  setIsOpenAddCreditCardModal: (isOpen: boolean) => void;
  isSecondary?: boolean;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  paymentMethodOptionTitle: {
    marginLeft: theme.spacing(1),
  },
  optionIcon: {
    width: '16px',
    height: '16px',
    color: theme.palette.darkBackground?.light,
  },
  autocompleteInput: {
    '& .MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
      padding: theme.spacing(0.375, 1),

      '& .MuiInputBase-input.MuiAutocomplete-input': {
        paddingLeft: '0px ',
      },
    },
  },
  inputStartIcon: {
    alignSelf: 'center',
    color: theme.palette.darkBackground?.light,

    '& svg': {
      width: '1rem',
      height: '1rem',
    },
  },
  addOption: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(0.375),
    '&.MuiMenuItem-root.Mui-selected': {
      color: theme.palette.background.paper,
      fill: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main,
    },
  },
  addNewBtn: {
    padding: theme.spacing(1.5),
    '& p': {
      width: '100%',
      textAlign: 'center',
    },
  },
  cardContainer: {
    backgroundColor: 'white',
    lineHeight: 0,
    padding: '1px 2px',
    margin: '-1px -2px',
    borderRadius: '2px',
  },
}));

const PaymentAccountField = (props: IPaymentAccountFieldProps): JSX.Element => {
  const {
    subscription,
    index,
    isNewCardInputDisabled,
    storedCreditCards,
    setIsOpenAddCreditCardModal,
    isSecondary,
  } = props;

  const { formState, control } = useFormContext();
  const { errors } = formState;
  const renderIntlMessage = useRenderIntlMessage();
  const classes = useStyles();

  const paymentAccountPath = isSecondary ? 'secondaryPaymentAccount' : 'paymentAccount';

  return (
    <Controller
      control={control}
      name={`subscriptions.${index}.${paymentAccountPath}`}
      defaultValue={
        isSecondary ? subscription.secondaryPaymentAccount : subscription.paymentAccount
      }
      render={({ field }) => (
        <Autocomplete
          multiple={false}
          value={field.value}
          onChange={(_, card: IPaymentAccount) => field.onChange(card)}
          onBlur={field.onBlur}
          disableClearable
          disabled={isNewCardInputDisabled}
          size="small"
          options={storedCreditCards?.toJS()}
          renderOption={(card: IPaymentAccount) => {
            const CreditCardIcon = getCreditCardIcon(card.creditCardType);

            return (
              <Box display="flex">
                <div className={classes.cardContainer}>
                  <CreditCardIcon className={classes.optionIcon} />
                </div>

                <Typography
                  color={card.expired ? 'error' : 'initial'}
                  className={classes.paymentMethodOptionTitle}
                >
                  {`*${card.lastFour} ${card ? getExpiryDate(card.expDate) : ''}`}
                </Typography>
              </Box>
            );
          }}
          getOptionLabel={(option: IPaymentAccount) =>
            option.lastFour ? `*${option.lastFour} ${getExpiryDate(option.expDate)}` : ''
          }
          getOptionSelected={(option: IPaymentAccount, selectedOption: IPaymentAccount) =>
            selectedOption?.id === option?.id
          }
          inputValue={field.value?.lastFour}
          renderInput={params => {
            const CreditCardIcon = getCreditCardIcon(
              isSecondary
                ? subscription.secondaryPaymentAccount?.creditCardType
                : subscription.paymentAccount?.creditCardType,
            );

            return (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment className={classes.inputStartIcon} position="start">
                      <CreditCardIcon />
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
                variant="outlined"
                className={classes.autocompleteInput}
                label={null}
                error={
                  !!getAccessByPropPath(errors, `subscriptions.${index}.${paymentAccountPath}`)
                }
                helperText={renderIntlMessage(
                  getAccessByPropPath(errors, `subscriptions.${index}.${paymentAccountPath}`)
                    ?.message,
                )}
              />
            );
          }}
          PaperComponent={({ children, ...other }) => {
            return (
              <Paper {...other}>
                <MenuItem
                  className={classes.addNewBtn}
                  onClick={() => setIsOpenAddCreditCardModal(true)}
                  onMouseDown={e => e.preventDefault()}
                >
                  <Typography>
                    <FormattedMessage {...commonMessages.addNewBtn} />
                  </Typography>
                </MenuItem>
                {children}
              </Paper>
            );
          }}
        />
      )}
    />
  );
};

export default PaymentAccountField;
